import React, { useState } from 'react';
import { useToast } from '../context/ToastContext';
import fetchWithAuth from '../utils/fetchWithAuth';
import { apiConfig } from '../config.js';

export interface FileUploaderProps {
  /** previously‐uploaded URL (unused internally, but kept for your consumers) */
  value?: string;
  /** current image URL to show a preview */
  currentImage?: string;
  /** called once a new URL comes back from the server */
  onUpload: (url: string) => void;
  /** optional label for the button */
  label?: string;
  /** file input accept string */
  accept?: string;
  /** hook so you can mark your form as dirty */
  onFileSelect?: () => void;
  /** distinguishes avatar / recipe / other uploads */
  type: 'avatar' | 'recipe' | 'other';
}

/**
 * Resize & compress to maxW×maxH and output a JPEG@80% blob
 */
const resizeImage = (file: File, maxW: number, maxH: number): Promise<File> => {
  return new Promise<File>((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();

    reader.onload = (e) => {
      img.onload = () => {
        const ratio = Math.min(maxW / img.width, maxH / img.height, 1);
        const canvas = document.createElement('canvas');
        canvas.width = img.width * ratio;
        canvas.height = img.height * ratio;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          return reject('Could not get canvas context');
        }
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        canvas.toBlob(
          (blob) => {
            if (!blob) return reject('Canvas is empty');
            // rename extension to .jpg
            const name = file.name.replace(/\.\w+$/, '.jpg');
            resolve(new File([blob], name, { type: 'image/jpeg' }));
          },
          'image/jpeg',
          0.8
        );
      };
      img.onerror = () => reject('Image load error');
      img.src = e.target?.result as string;
    };

    reader.onerror = () => reject('FileReader error');
    reader.readAsDataURL(file);
  });
};

const FileUploader: React.FC<FileUploaderProps> = ({
  value,
  currentImage,
  onUpload,
  onFileSelect,
  label = 'Choose Photo',
  accept = 'image/*',
  type,
}) => {
  const [uploading, setUploading] = useState(false);
  const { showToast } = useToast() as any;

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    onFileSelect?.();

    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      // client‐side resize+compress
      const resizedFile = await resizeImage(file, 1024, 1024);

      const formData = new FormData();
      formData.append('file', resizedFile);
      formData.append('type', type);

      const res = await fetch(`${apiConfig.apiBaseUrl}/api/uploads`, {
        method: 'POST',
        body: formData,
      });
      const data = await res.json();

      if (res.ok && data.url) {
        onUpload(data.url);
      } else {
        showToast({
          message: data.error || 'Upload failed.',
          type: 'error',
        });
      }
    } catch (err: any) {
      console.error('Upload error:', err);
      showToast({ message: 'Unexpected upload error.', type: 'error' });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      {/* optional preview */}
      {currentImage && (
        <img
          src={currentImage}
          alt="Current"
          className="mb-2 max-w-full rounded"
        />
      )}

      <label className="rounded-full border border-black bg-white px-4 py-1 text-black block my-1">
        {uploading ? 'Uploading...' : label}
        <input
          type="file"
          accept={accept}
          onChange={handleFileChange}
          className="hidden"
        />
      </label>
    </div>
  );
};

export default FileUploader;
